import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "antd";
import { FaExclamationCircle } from "react-icons/fa";

const MIN_SEAT_SIZE = 0.1;
const MAX_SEAT_SIZE = 99;
const SEAT_STEP = 0.1;
// Grosor del borde del círculo dibujado en el preview (en px de pantalla).
// Debe coincidir con el `border` definido en el SCSS de los asientos.
const SEAT_BORDER_WIDTH = 4;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const average = (values) => {
    if (!values.length) return 0;
    return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const median = (values) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);

    if (sorted.length % 2 === 0) {
        return (sorted[mid - 1] + sorted[mid]) / 2;
    }

    return sorted[mid];
};

const percentile = (values, p) => {
    if (!values.length) return 0;
    const sorted = [...values].sort((a, b) => a - b);
    const index = Math.min(
        sorted.length - 1,
        Math.max(0, Math.round((sorted.length - 1) * p))
    );
    return sorted[index];
};

const getPixelLuminance = (data, width, x, y) => {
    const px = Math.max(0, Math.min(width - 1, Math.round(x)));
    const py = Math.max(0, Math.round(y));
    const index = (py * width + px) * 4;

    const r = data[index];
    const g = data[index + 1];
    const b = data[index + 2];

    return 0.299 * r + 0.587 * g + 0.114 * b;
};

const getNeighborhoodStats = (data, width, height, centerX, centerY, radius = 2) => {
    const values = [];

    for (let y = -radius; y <= radius; y += 1) {
        for (let x = -radius; x <= radius; x += 1) {
            const px = Math.round(centerX + x);
            const py = Math.round(centerY + y);

            if (px < 0 || py < 0 || px >= width || py >= height) continue;

            values.push(getPixelLuminance(data, width, px, py));
        }
    }

    if (!values.length) {
        return {
            min: 0,
            max: 0,
            median: 0,
            average: 0,
        };
    }

    const sorted = [...values].sort((a, b) => a - b);

    return {
        min: sorted[0],
        max: sorted[sorted.length - 1],
        median: sorted[Math.floor(sorted.length / 2)],
        average: average(values),
    };
};

const measureSeatBoundsFromImage = ({
    imageData,
    width,
    height,
    centerX,
    centerY,
    fallbackSize = 16,
}) => {
    if (
        centerX < 0 ||
        centerY < 0 ||
        centerX >= width ||
        centerY >= height
    ) {
        return {
            width: fallbackSize,
            height: fallbackSize,
            confidence: 0,
        };
    }

    const data = imageData.data;
    const stats = getNeighborhoodStats(data, width, height, centerX, centerY, 2);
    const referenceLum = stats.median;

    const maxScan = Math.min(80, Math.floor(Math.min(width, height) / 5));
    const edgeThreshold = 22;
    const consecutiveNeeded = 3;

    const scanDirection = (dx, dy) => {
        let consecutive = 0;

        for (let step = 1; step <= maxScan; step += 1) {
            const x = centerX + dx * step;
            const y = centerY + dy * step;

            if (x < 0 || y < 0 || x >= width || y >= height) {
                return step;
            }

            const lum = getPixelLuminance(data, width, x, y);
            const diff = Math.abs(lum - referenceLum);

            if (diff >= edgeThreshold) {
                consecutive += 1;
                if (consecutive >= consecutiveNeeded) {
                    return step - consecutiveNeeded + 1;
                }
            } else {
                consecutive = 0;
            }
        }

        return maxScan;
    };

    const left = scanDirection(-1, 0);
    const right = scanDirection(1, 0);
    const up = scanDirection(0, -1);
    const down = scanDirection(0, 1);

    const measuredWidth = left + right;
    const measuredHeight = up + down;

    const aspectRatio =
        measuredWidth > 0 && measuredHeight > 0
            ? Math.max(measuredWidth, measuredHeight) /
            Math.max(1, Math.min(measuredWidth, measuredHeight))
            : 999;

    let confidence = 0.85;

    if (measuredWidth < 4 || measuredHeight < 4) {
        confidence = 0.2;
    } else if (aspectRatio > 3.2) {
        confidence = 0.35;
    } else if (aspectRatio > 2.4) {
        confidence = 0.5;
    }

    return {
        width: measuredWidth,
        height: measuredHeight,
        confidence,
    };
};

const getNearestNeighborDistances = (placedSeats = []) => {
    const distances = [];

    for (let i = 0; i < placedSeats.length; i += 1) {
        const current = placedSeats[i];
        const localDistances = [];

        for (let j = 0; j < placedSeats.length; j += 1) {
            if (i === j) continue;

            const other = placedSeats[j];
            const dx = other.x - current.x;
            const dy = other.y - current.y;
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance > 2) {
                localDistances.push({
                    distance,
                    dx: Math.abs(dx),
                    dy: Math.abs(dy),
                });
            }
        }

        if (!localDistances.length) continue;

        localDistances.sort((a, b) => a.distance - b.distance);

        const closest = localDistances[0];
        distances.push(closest.distance);
    }

    return distances;
};

const getAxisAwareSpacing = (placedSeats = []) => {
    const horizontalDistances = [];
    const verticalDistances = [];
    const genericDistances = [];

    for (let i = 0; i < placedSeats.length; i += 1) {
        const current = placedSeats[i];

        for (let j = i + 1; j < placedSeats.length; j += 1) {
            const other = placedSeats[j];
            const dx = other.x - current.x;
            const dy = other.y - current.y;

            const absDx = Math.abs(dx);
            const absDy = Math.abs(dy);
            const distance = Math.sqrt(dx * dx + dy * dy);

            if (distance <= 2) continue;

            genericDistances.push(distance);

            if (absDx > absDy * 1.75) {
                horizontalDistances.push(distance);
            } else if (absDy > absDx * 1.75) {
                verticalDistances.push(distance);
            }
        }
    }

    return {
        horizontal: horizontalDistances,
        vertical: verticalDistances,
        generic: genericDistances,
    };
};

const removeOutliers = (values = []) => {
    if (values.length <= 4) return values;

    const low = percentile(values, 0.15);
    const high = percentile(values, 0.85);

    return values.filter((value) => value >= low && value <= high);
};

const getSeatSizeFromSpacing = (placedSeats = [], fallbackSeatSize = 16) => {
    if (!Array.isArray(placedSeats) || placedSeats.length < 2) {
        return fallbackSeatSize;
    }

    const nearestNeighborDistances = getNearestNeighborDistances(placedSeats);
    const cleanedNearest = removeOutliers(nearestNeighborDistances);

    const axisSpacing = getAxisAwareSpacing(placedSeats);

    const horizontal = removeOutliers(axisSpacing.horizontal);
    const vertical = removeOutliers(axisSpacing.vertical);

    const candidates = [];

    if (cleanedNearest.length) {
        candidates.push(median(cleanedNearest) * 0.72);
    }

    if (horizontal.length) {
        candidates.push(median(horizontal) * 0.72);
    }

    if (vertical.length) {
        candidates.push(median(vertical) * 0.72);
    }

    if (!candidates.length) {
        return fallbackSeatSize;
    }

    return clamp(median(candidates), MIN_SEAT_SIZE, MAX_SEAT_SIZE);
};

const getSuggestedSeatSize = async ({
    imgSrc,
    placedSeats,
    offset,
    scale,
    fallbackSeatSize,
}) => {
    const normalizedSeats = placedSeats.map((seat) => ({
        ...seat,
        x: (seat.x - offset.x) / scale,
        y: (seat.y - offset.y) / scale,
    }));

    const sizeFromSpacing = getSeatSizeFromSpacing(
        normalizedSeats,
        fallbackSeatSize
    );

    if (!imgSrc || !Array.isArray(placedSeats) || placedSeats.length === 0) {
        return Number(
            clamp(sizeFromSpacing, MIN_SEAT_SIZE, MAX_SEAT_SIZE).toFixed(1)
        );
    }

    const img = new Image();
    img.src = imgSrc;

    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
    });

    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;

    const ctx = canvas.getContext("2d");

    if (!ctx) {
        return Number(
            clamp(sizeFromSpacing, MIN_SEAT_SIZE, MAX_SEAT_SIZE).toFixed(1)
        );
    }

    ctx.drawImage(img, 0, 0);

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const imageSizes = [];

    placedSeats.forEach((seat) => {
        const imageX = (seat.x - offset.x) / scale;
        const imageY = (seat.y - offset.y) / scale;

        const result = measureSeatBoundsFromImage({
            imageData,
            width: canvas.width,
            height: canvas.height,
            centerX: imageX,
            centerY: imageY,
            fallbackSize: fallbackSeatSize,
        });

        const localSize = Math.max(result.width, result.height) / 2;

        if (
            result.confidence > 0.4 &&
            Number.isFinite(localSize) &&
            localSize >= MIN_SEAT_SIZE * 0.5
        ) {
            imageSizes.push(localSize);
        }
    });

    const filteredImageSizes = removeOutliers(imageSizes);
    const sizeFromImage = filteredImageSizes.length
        ? median(filteredImageSizes)
        : sizeFromSpacing;
    const combined =
        sizeFromSpacing * 0.65 +
        sizeFromImage * 0.35;

    // El círculo del preview se dibuja con box-sizing: border-box, por lo que el
    // borde queda DENTRO del diámetro. Para que el asiento visible coincida con el
    // asiento de la imagen (cuyo trazo se mide hasta el borde exterior), se descuenta
    // el grosor del borde por ambos lados, expresado en unidades de seatSize (sin escalar).
    const borderCompensation = (SEAT_BORDER_WIDTH * 2) / (scale || 1);
    const adjusted = combined - borderCompensation;

    return Number(
        clamp(adjusted, MIN_SEAT_SIZE, MAX_SEAT_SIZE).toFixed(1)
    );
};

const CalibrationSeatSizeConfirmStep = ({
    onNext,
    onBack,
    onClose,
    imgSrc,
    viewState,
    placedSeats = [],
    initialSeatSize = 16,
    onSeatSizeChange,
}) => {
    const [seatSize, setSeatSize] = useState(initialSeatSize);
    const [isAutoAdjusting, setIsAutoAdjusting] = useState(true);
    const [hasAutoAdjusted, setHasAutoAdjusted] = useState(false);
    const [seatSizeScaled, setSeatSizeScaled] = useState(initialSeatSize);
    const autoAdjustedRef = useRef(false);

    const { zoom, offset } = viewState || {
        zoom: 300,
        offset: { x: 0, y: 0 },
    };

    const scale = (viewState?.zoom || 100) / 100;

    useEffect(() => {
        let cancelled = false;

        const runAutoAdjust = async () => {
            if (autoAdjustedRef.current) return;

            if (!imgSrc || !placedSeats.length) {
                setIsAutoAdjusting(false);
                return;
            }

            try {
                setIsAutoAdjusting(true);

                const suggestedSize = await getSuggestedSeatSize({
                    imgSrc,
                    placedSeats,
                    offset,
                    scale,
                    fallbackSeatSize: initialSeatSize,
                });

                if (cancelled) return;
                setSeatSize(suggestedSize);
                setSeatSizeScaled(suggestedSize * scale);
                setHasAutoAdjusted(true);
                autoAdjustedRef.current = true;

                if (onSeatSizeChange) {
                    onSeatSizeChange(suggestedSize);
                }
            } catch (error) {
                if (!cancelled) {
                    setSeatSize(initialSeatSize);
                    setSeatSizeScaled(initialSeatSize * scale);
                }
            } finally {
                if (!cancelled) {
                    setIsAutoAdjusting(false);
                }
            }
        };

        runAutoAdjust();

        return () => {
            cancelled = true;
        };
    }, [imgSrc, placedSeats, viewState, initialSeatSize, onSeatSizeChange, offset, scale]);

    const imageLayerStyle = useMemo(() => {
        return {
            transform: `translate(${offset.x}px, ${offset.y}px) scale(${zoom / 100})`,
            transformOrigin: "top left",
        };
    }, [offset, zoom]);

    const updateSeatSize = (direction) => {
        setSeatSize((prev) => {
            const nextValue =
                direction === "up"
                    ? prev + SEAT_STEP
                    : prev - SEAT_STEP;

            const finalValue = Number(
                clamp(nextValue, MIN_SEAT_SIZE, MAX_SEAT_SIZE).toFixed(1)
            );

            if (onSeatSizeChange) {
                onSeatSizeChange(finalValue);
            }

            setSeatSizeScaled(finalValue * scale);

            return finalValue;
        });
    };

    const handleSeatSizeInputChange = (event) => {
        const rawValue = event.target.value;

        // Permite que el campo quede vacío o en escritura intermedia (ej. "18.")
        if (rawValue === "" || rawValue === ".") {
            setSeatSize(rawValue);
            return;
        }

        const parsed = Number(rawValue);
        if (Number.isNaN(parsed)) return;

        setSeatSize(parsed);
        setSeatSizeScaled(parsed * scale);

        if (onSeatSizeChange) {
            onSeatSizeChange(parsed);
        }
    };

    const commitSeatSizeInput = () => {
        setSeatSize((prev) => {
            const numericValue = Number(prev);
            const safeValue = Number.isFinite(numericValue) ? numericValue : MIN_SEAT_SIZE;

            const finalValue = Number(
                clamp(safeValue, MIN_SEAT_SIZE, MAX_SEAT_SIZE).toFixed(1)
            );

            setSeatSizeScaled(finalValue * scale);

            if (onSeatSizeChange) {
                onSeatSizeChange(finalValue);
            }

            return finalValue;
        });
    };

    const displaySeatSize =
        typeof seatSize === "number" ? seatSize.toFixed(1) : seatSize;

    return (
        <div className="calibration-size-confirm-step">
            <button
                type="button"
                className="calibration-size-confirm-step__close"
                onClick={onClose}
            >
                ✕
            </button>

            <div className="calibration-size-confirm-step__wrapper">
                <div className="calibration-size-confirm-step__notice">
                    <div className="calibration-size-confirm-step__notice-badge">
                        <FaExclamationCircle />
                    </div>

                    <div className="calibration-size-confirm-step__notice-content">
                        <h1>Ajustamos automáticamente el tamaño de los asientos</h1>
                        <p>
                            Este es el tamaño recomendado para que el mapa se vea claro y bien proporcionado.
                        </p>
                    </div>
                </div>

                <div className="calibration-size-confirm-step__preview-card">
                    <div className="calibration-size-confirm-step__preview">
                        {imgSrc && (
                            <div
                                className="calibration-size-confirm-step__image-layer"
                                style={imageLayerStyle}
                            >
                                <img
                                    src={imgSrc}
                                    alt="Reference chart"
                                    draggable={false}
                                />
                            </div>
                        )}

                        {placedSeats.map((seat) => (
                            <div
                                key={seat.id}
                                className="calibration-size-confirm-step__seat"
                                style={{
                                    width: `${seatSizeScaled}px`,
                                    height: `${seatSizeScaled}px`,
                                    left: `${seat.x}px`,
                                    top: `${seat.y}px`,
                                    transform: "translate(-50%, -50%)",
                                }}
                            />
                        ))}
                    </div>
                </div>

                <div className="calibration-size-confirm-step__bottom">
                    <div className="calibration-size-confirm-step__help">
                        <div className="calibration-size-confirm-step__step-badge">3</div>

                        <div className="calibration-size-confirm-step__help-content">
                            <h2>Verifica el tamaño</h2>
                            <p>
                                Puedes ajustarlo si no coincide con tu layout.
                            </p>
                        </div>
                    </div>

                    <div className="calibration-size-confirm-step__size-box">
                        <span>Tamaño del asiento</span>

                        <div
                            className={`calibration-size-confirm-step__size-control ${hasAutoAdjusted ? "is-highlighted" : ""}`}
                        >
                            <button
                                type="button"
                                onClick={() => updateSeatSize("down")}
                                disabled={Number(seatSize) <= MIN_SEAT_SIZE || isAutoAdjusting}
                            >
                                ‹
                            </button>

                            {isAutoAdjusting ? (
                                <span>...</span>
                            ) : (
                                <span className="calibration-size-confirm-step__size-input-wrapper">
                                    <input
                                        type="number"
                                        className="calibration-size-confirm-step__size-input"
                                        value={displaySeatSize}
                                        min={MIN_SEAT_SIZE}
                                        max={MAX_SEAT_SIZE}
                                        step={SEAT_STEP}
                                        onChange={handleSeatSizeInputChange}
                                        onBlur={commitSeatSizeInput}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter") {
                                                event.currentTarget.blur();
                                            }
                                        }}
                                    />
                                    <span className="calibration-size-confirm-step__size-unit">pt</span>
                                </span>
                            )}

                            <button
                                type="button"
                                onClick={() => updateSeatSize("up")}
                                disabled={Number(seatSize) >= MAX_SEAT_SIZE || isAutoAdjusting}
                            >
                                ›
                            </button>
                        </div>
                    </div>
                </div>

                <div className="calibration-size-confirm-step__actions">
                    <Button type="link" onClick={onBack}>
                        Atras
                    </Button>

                    <Button type="link" onClick={onNext} disabled={isAutoAdjusting}>
                        Siguiente
                    </Button>
                </div>
            </div>
        </div>
    );
};

export { CalibrationSeatSizeConfirmStep };